import { Button, Icon, Row, Typography } from '@repo/ui/components'
import { useState, useEffect, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useAgentDeletePostPhoto } from '@repo/services'
import Image from 'next/image'
import { useErrorHandler, useTranslate } from '@repo/shared/hooks'

export interface UploadedImage {
  id: number
  url: string
  file: File | null
  name: string
  size: number
  status?: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

interface ImageUploaderProps {
  onUpload: (
    file: File,
    postId: number,
    callback: (success: boolean) => void,
  ) => void
  isLoading?: boolean
  className?: string
  accept?: string
  maxSize?: number
  initialImages?: UploadedImage[]
  onImagesChange?: (images: UploadedImage[]) => void
  onDelete?: (fileId: number) => void
  postId?: number
}

export function ImageUploader({
  isLoading = false,
  className = '',
  accept = 'image/*',
  maxSize = 5242880, // 5MB
  initialImages = [],
  onImagesChange,
  postId,
  onDelete,
}: ImageUploaderProps) {
  const { t } = useTranslate()
  const { errorHandler } = useErrorHandler()
  const [error, setError] = useState<string | null>(null)
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>(() =>
    initialImages.map((img) => ({ ...img, status: 'success' })),
  )

  const { mutate: deletePostPhoto } = useAgentDeletePostPhoto({
    invalidate: true,
  })

  // Only update from initialImages if the component is first mounted
  useEffect(() => {
    if (initialImages.length > 0 && uploadedImages.length === 0) {
      setUploadedImages(
        initialImages.map((img) => ({ ...img, status: 'success' })),
      )
    }
  }, [initialImages, uploadedImages.length])

  const updateImages = useCallback(
    (newImages: UploadedImage[]) => {
      setUploadedImages(newImages)
      onImagesChange?.(newImages)
    },
    [onImagesChange],
  )

  const getImage = async (file: File) => {
    try {
      const previewUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader()
        reader.onload = () => {
          resolve(reader.result as string)
        }
        reader.readAsDataURL(file)
      })

      const newImage: UploadedImage = {
        id: Number(Math.random().toString(10).substring(2, 15)),
        url: previewUrl,
        file: file,
        name: file.name,
        size: file.size,
        status: 'pending',
      }

      return newImage
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to process image'
      setError(errorMessage)
      return undefined
    }
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      [accept]: [],
    },
    maxSize,
    multiple: true,
    onDrop: async (acceptedFiles) => {
      setError(null)
      const files = acceptedFiles
      if (files) {
        const newImages = await Promise.all(
          files.map(async (file) => await getImage(file)),
        )
        const filteredNewImages = newImages.filter(
          (image) => image !== undefined,
        )
        const updatedImages = [...uploadedImages, ...filteredNewImages]
        updateImages(updatedImages as UploadedImage[])
      }
    },
    onDropRejected: () => {
      const errorMessage = 'File is too large or invalid format'
      setError(errorMessage)
    },
  })

  const handleDelete = (fileId: number, fileIndex: number) => {
    if (postId && fileId) {
      deletePostPhoto(
        { postId, photoId: fileId },
        {
          onSuccess: () => {
            const newImages = uploadedImages.filter((img) => img.id !== fileId)
            updateImages(newImages)

            onDelete?.(fileId)
          },
          onError: (error) => {
            errorHandler(error, {
              type: 'both',
            })
          },
        },
      )
    } else {
      const newImages = uploadedImages.filter(
        (img, index) => index !== fileIndex,
      )
      updateImages(newImages)
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed  rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-base-surface-80 bg-primary/5' : 'border-base-surface-60 hover:border-primary'}
          ${error ? 'border-red-500' : ''}`}
      >
        <input {...getInputProps()} />

        <div className='space-y-2 flex flex-col items-center justify-center'>
          <Image
            src='/images/add-image.webp'
            alt='add-image'
            width={48}
            height={48}
          />
          <p className='text-sm text-gray-500'>
            {/* {isDragActive
                ? 'Drop the file here'
                : 'Drag & drop an image here, or click to select'} */}

            <Row className='justify-center gap-xs'>
              <Icon name='Plus' size='sm' className='fill-base-surface-60' />
              <Typography size='sm' color='text-base-surface-100'>
                {t('dashboardAgent_add_image_and_video')}
              </Typography>
            </Row>
          </p>
          {/* <p className='text-xs text-gray-400'>
              Max file size: {maxSize / 1024 / 1024}MB
            </p> */}
        </div>
      </div>
      {error && <p className='text-sm text-red-500'>{error}</p>}
      {isLoading && (
        <div className='flex items-center justify-center'>
          <div className='animate-spin rounded-full h-6 w-6 border-b-2 border-primary'></div>
        </div>
      )}

      {/* Uploaded Images List */}
      {uploadedImages.length > 0 && (
        <div className='flex flex-wrap gap-2 mt-4'>
          {uploadedImages.map((image, index) => (
            <div
              key={image.id}
              className='flex items-center rounded-sm   relative '
            >
              <div className='relative'>
                <Image
                  src={image.url}
                  alt={image.name}
                  width={104}
                  height={104}
                  className='w-26 h-26 object-cover rounded-sm border'
                />
              </div>
              {/* <div className='flex-1 ml-4 flex flex-col items-start'>
                <span className='text-sm font-medium text-neutral-main'>
                  {image.name}
                </span>
                <span className='text-xs text-neutral-60'>
                  {(image.size / 1024).toFixed(1)} KB
                </span>
                {image.status === 'error' && (
                  <span className='text-xs text-red-500'>{image.error}</span>
                )}
                {image.status === 'uploading' && (
                  <span className='text-xs text-blue-500'>Uploading...</span>
                )}
                {image.status === 'pending' && (
                  <span className='text-xs text-yellow-500'>Pending</span>
                )}
              </div> */}
              <div className='flex items-center gap-2 absolute left-[10px] bottom-[10px] '>
                <Button
                  type='button'
                  variant='fill'
                  color='negative'
                  size='sm'
                  onClick={() => handleDelete(image.id, index)}
                >
                  <Icon name='TrashBin' />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
